import { useNavigate } from '@tanstack/react-router';
import moment from 'moment';
import { AnimatePresence } from 'motion/react';
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useInView } from 'react-intersection-observer';
import { useSWRConfig } from 'swr';

import CrossIcon from '@common/assets/icons/cross.svg?react';
import HeartIcon from '@common/assets/icons/heart.svg?react';
import { useTabbar } from '@common/providers/useTabbar';
import { useAppStore } from '@common/stores/useAppStore';
import { BottomSheet } from '@common/ui/BottomSheet';
import { Button } from '@common/ui/buttons/Button';
import { IconButton } from '@common/ui/buttons/IconButton';
import { Disclaimer } from '@common/ui/Disclaimer';
import { Loader } from '@common/ui/Loader';
import { Result } from '@common/ui/Result';
import { useEventEmit } from '@common/utils/events';
import {
  getRetryAfterMs,
  isRateLimitedError,
  postEvent,
} from '@common/utils/fetcher';

import { useAuthStore } from '@entities/auth/stores/useAuthStore';
import { useFiltersStore } from '@entities/dating/stores/useFiltersStore';
import {
  AdvertisingCard,
  AdvertisingCardSkeleton,
} from '@entities/dating/ui/AdvertisingCard';
import { BackButton } from '@entities/dating/ui/BackButton';
import { FiltersButton } from '@entities/dating/ui/FiltersButton';
import { MatchDialog } from '@entities/dating/ui/MatchDialog';
import { ProfileCard } from '@entities/dating/ui/ProfileCard';
import { ProfileInfo } from '@entities/dating/ui/ProfileInfo';

import { analytics } from '@services/AnalyticsService';
import {
  FEED_PAGE_MAX_ENTRIES,
  FeedRequestSkipped,
  type FeedAdvertising,
  type FeedResponse,
} from '@services/FeedService';
import { feedAdQueryKey, useGetFeedAd } from '@services/hooks/FeedAdService';
import { getFeedQueryKey, useGetFeed } from '@services/hooks/FeedService';
import { usePostSwipe } from '@services/hooks/MatchesService';
import { type User } from '@services/UsersService';

import { mergeFeedProfiles } from '@widgets/dating/utils/mergeFeedProfiles';
import {
  rememberSwipedId,
  withoutSwipedIds,
} from '@widgets/dating/utils/swipedIds';

const GENDERS = {
  male: 0,
  female: 1,
} as const;

const VISIBLE_CARDS_COUNT = 3;
// Automatic fetching (FEED-CORE 5.9 item 3, as refined in review). After a
// page that adds no new card, or says the feed has no more, the list fetches
// again on its own only after a swipe, a filter change, or a delay: 60 s,
// doubling with each such page in a row up to 15 minutes. A request that
// fails or brings no page is retried after 5 s, doubling up to 60 s. Timed
// retries run only while the page is visible; one that falls due while it is
// hidden runs when it is shown again.
const FEED_RETRY_AFTER_MS = 60 * 1000;
const FEED_RETRY_MAX_MS = 15 * 60 * 1000;
const FEED_FAILURE_RETRY_MS = 5 * 1000;
const FEED_FAILURE_RETRY_MAX_MS = 60 * 1000;

// A swipe refused by the request rate limits (errors.code RATE_LIMITED) is
// not the daily swipe limit: no paywall, a neutral notice, and no swipe until
// the server's Retry-After has passed (this long when it gives none).
const SWIPE_RATE_LIMIT_PAUSE_MS = 2 * 1000;
const SWIPE_RATE_LIMITED_NOTICE = {
  title: 'Слишком много действий',
  description: 'Попробуйте ещё раз чуть позже',
};

const doublingDelay = (baseMs: number, maxMs: number, inARow: number) =>
  Math.min(baseMs * 2 ** Math.max(0, inARow - 1), maxMs);

/**
 * The next page's `limit` (FEED-CORE 5.6): the room left in the deck,
 * counted in entries with ad placeholders, as the server counts them. A free
 * user keeps `remaining` entries in all, a premium user (`remaining` null)
 * 70. Below 1 there is nothing to fetch. Without a swipe status (undefined)
 * the list fetches nothing, as before.
 */
const feedRequestLimit = (
  remaining: number | null | undefined,
  held: number,
) => {
  if (remaining === undefined) return 0;

  return Math.min(
    (remaining ?? FEED_PAGE_MAX_ENTRIES) - held,
    FEED_PAGE_MAX_ENTRIES,
  );
};

const feedSWRRuntimeOptions = {
  revalidateOnMount: false,
  revalidateOnFocus: false,
  revalidateOnReconnect: false,
  revalidateIfStale: false,
  // The list reads only what its own requests return, so SWR's own retries
  // of a failed request would be wasted; the list retries itself.
  shouldRetryOnError: false,
};
const feedAdSWRRuntimeOptions = {
  revalidateOnMount: true,
  revalidateOnFocus: false,
  revalidateOnReconnect: false,
  revalidateIfStale: false,
  dedupingInterval: Infinity,
};

export const ProfilesList: React.FC = () => {
  const tabbar = useTabbar();
  const navigate = useNavigate();
  const emit = useEventEmit();
  const auth = useAuthStore();
  const { cache } = useSWRConfig();

  const [ref, isInView] = useInView({
    rootMargin: '0px 0px -100px 0px',
  });

  const { filters, getFiltersCount } = useFiltersStore();

  const { tasks: responseTasks } = useAppStore();
  const feedQueryParams = {
    looking_for:
      auth?.user?.sex === GENDERS.female ? GENDERS.male : GENDERS.female,
    age_from: filters.age?.from || undefined,
    age_to: filters.age?.to || undefined,
    has_about: filters.hasAbout || undefined,
    is_verified: filters.isVerified || undefined,
    goal: filters.goals || undefined,
    alcohol: filters.alcoholViews || undefined,
    smoking: filters.smokingViews || undefined,
    children: filters.hasChildren || undefined,
    zodiac: filters.zodiac || undefined,
    height_from: filters.height?.from || undefined,
    height_to: filters.height?.to || undefined,
  };
  // The request's identity: it changes when, and only when, a filter does.
  const feedKey = getFeedQueryKey(feedQueryParams);
  // SWR's mutate resolves with its cache when a request fails, so the list
  // learns of a failure from onError: the key of the last failed request.
  const failedFeedKeyRef = useRef<string | null>(null);
  const feedSWROptions = useMemo(
    () => ({
      ...feedSWRRuntimeOptions,
      onError: (error: unknown, key: string) => {
        // A request SWR started on its own, skipped without a request (see
        // readFeedLimit). It says nothing about the list's own request, and
        // can settle after the list cleared this ref for that request.
        if (error instanceof FeedRequestSkipped) return;
        failedFeedKeyRef.current = key;
      },
    }),
    [],
  );

  const { trigger: triggerPostSwipe } = usePostSwipe();

  const [profiles, setProfiles] = useState<FeedResponse[]>([]);
  const [skippedProfile, setSkippedProfile] = useState<User>();
  const [isShowDisclaimer, setIsShowDisclaimer] = useState(false);
  const [isShowMatchDialog, setIsShowMatchDialog] = useState(false);
  const [isShowNoMoreSwipeError, setIsShowNoMoreSwipeError] = useState(false);
  // True while swipes wait out a rate limit's Retry-After. The ref is read by
  // the bottom sheet's buttons, whose handlers are set by an effect.
  const [isSwipePaused, setIsSwipePaused] = useState(false);
  const isSwipePausedRef = useRef(false);
  const swipePauseTimerRef = useRef<ReturnType<typeof setTimeout>>();
  const [matchProfile, setMatchProfile] = useState<User>();
  const [isFetched, setIsFetched] = useState(false);
  // The feed key that automatic fetching is stopped for, or null. A filter
  // change makes it another key, so the stop no longer applies.
  const [stoppedFeedKey, setStoppedFeedKey] = useState<string | null>(null);
  // Bumped to run the fetch effect again, for a page dropped because the
  // filters changed while it was requested.
  const [refetchToken, setRefetchToken] = useState(0);
  // The cards swiped while the list is mounted, bounded; an undo or a failed
  // swipe forgets its card. Every incoming page drops them.
  const [swipedIds] = useState(() => new Set<string>());
  const isMountedRef = useRef(false);
  const isFetchingFeedRef = useRef(false);
  const retryTimerRef = useRef<ReturnType<typeof setTimeout>>();
  const isRetryDueRef = useRef(false);
  const failuresInARowRef = useRef(0);
  const idlePagesInARowRef = useRef(0);
  const latestFeedKeyRef = useRef(feedKey);
  const profilesRef = useRef(profiles);
  const remainingRef = useRef(auth?.swipe_status?.remaining);
  // True only while loadMoreProfiles starts its request.
  const isListRequestRef = useRef(false);

  useLayoutEffect(() => {
    profilesRef.current = profiles;
  }, [profiles]);

  useLayoutEffect(() => {
    remainingRef.current = auth?.swipe_status?.remaining;
  }, [auth?.swipe_status?.remaining]);

  useLayoutEffect(() => {
    latestFeedKeyRef.current = feedKey;
  }, [feedKey]);

  // The page's `limit`, read by the fetcher when a request starts. It is not
  // a query param: a swipe changes it, and must not change the SWR key.
  // Only the list's own requests get one. SWR also fetches a new key on its
  // own (on a filter change); that request gets 0, so getFeed skips it
  // without a request: nobody would merge its page, yet the server would
  // record it as served.
  const readFeedLimit = useCallback(
    () =>
      isListRequestRef.current
        ? feedRequestLimit(remainingRef.current, profilesRef.current.length)
        : 0,
    [],
  );
  const {
    mutate: mutateFeed,
    isLoading: isLoadingFeed,
    isValidating: isValidatingFeed,
  } = useGetFeed({
    queryParams: feedQueryParams,
    getLimit: readFeedLimit,
    swrOptions: feedSWROptions,
  });

  const clearRetry = useCallback(() => {
    clearTimeout(retryTimerRef.current);
    retryTimerRef.current = undefined;
    isRetryDueRef.current = false;
  }, []);

  const stopAutoFetch = useCallback(
    (key: string, retryAfterMs: number) => {
      clearRetry();
      setStoppedFeedKey(key);
      retryTimerRef.current = setTimeout(() => {
        retryTimerRef.current = undefined;
        if (document.visibilityState !== 'visible') {
          isRetryDueRef.current = true;
          return;
        }
        setStoppedFeedKey(null);
      }, retryAfterMs);
    },
    [clearRetry],
  );

  const resumeAutoFetch = useCallback(() => {
    clearRetry();
    failuresInARowRef.current = 0;
    idlePagesInARowRef.current = 0;
    setStoppedFeedKey(null);
  }, [clearRetry]);

  useEffect(() => {
    isMountedRef.current = true;
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible' && isRetryDueRef.current) {
        isRetryDueRef.current = false;
        setStoppedFeedKey(null);
      }
    };
    document.addEventListener('visibilitychange', onVisibilityChange);

    return () => {
      isMountedRef.current = false;
      clearTimeout(retryTimerRef.current);
      clearTimeout(swipePauseTimerRef.current);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, []);

  // A filter change lifts any stop and starts the back-off over.
  useEffect(() => {
    clearRetry();
    failuresInARowRef.current = 0;
    idlePagesInARowRef.current = 0;
    setStoppedFeedKey(null);
  }, [clearRetry, feedKey]);

  const isAutoFetchStopped = stoppedFeedKey === feedKey;

  const activeTasksCount = useMemo(() => {
    return (responseTasks || []).filter((task) => task.status === 'available')
      ?.length;
  }, [responseTasks]);

  const currentButtons = useMemo(() => {
    if (auth?.user?.is_premium && activeTasksCount === 0) {
      return null;
    }

    return (
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '8px',
          width: '100%',
        }}
      >
        {activeTasksCount > 0 && (
          <Button
            testId="profiles-list-tasks-button"
            variant="premiumGradient"
            size={1000}
            onClick={() => navigate({ to: '/tasks' })}
          >
            Выполнить задания
          </Button>
        )}

        {!auth?.user?.is_premium && (
          <Button
            testId="profiles-list-premium-button"
            variant={
              activeTasksCount > 0 ? 'actionSecondary' : 'premiumGradient'
            }
            size={1000}
            onClick={() => navigate({ to: '/premium' })}
          >
            Подключить Premium
          </Button>
        )}
      </div>
    );
  }, [activeTasksCount, auth?.user]);

  const emptyResultDescriptionText = useMemo(() => {
    if (!auth?.user?.is_premium && activeTasksCount === 0) {
      return 'А пока подключи Premium, чтобы свайпать без ограничений';
    }

    if (!auth?.user?.is_premium && activeTasksCount > 0) {
      return 'А пока выполни задания или подключи Premium';
    }

    if (auth?.user?.is_premium && activeTasksCount === 0) {
      return 'Если у вас уже есть мэтч, то напишите ему скорее';
    }

    if (auth?.user?.is_premium && activeTasksCount > 0) {
      return 'А пока выполните задания, чтобы продлить Premium';
    }
  }, [activeTasksCount, auth?.user]);

  const hasSwipes =
    auth?.swipe_status?.remaining === null || auth?.swipe_status?.remaining > 0;

  const loadMoreProfiles = useCallback(async () => {
    // One request at a time: SWR's mutate starts a new request even while
    // one is in flight.
    if (isFetchingFeedRef.current) {
      return;
    }
    // The deck now, and so the page's `limit`: mutateFeed calls the fetcher
    // synchronously, and readFeedLimit reads these same refs.
    const remaining = remainingRef.current;
    const held = profilesRef.current.length;
    const limit = feedRequestLimit(remaining, held);
    // No room: nothing to fetch, and nothing armed. The fetch effect runs
    // again when the deck shrinks or the remaining swipes grow.
    if (limit < 1) {
      return;
    }
    // Where the deck is cut. Free: at `remaining`, as before. Premium: at
    // held + limit, not at the page's length, so that every card the server
    // recorded for this page is kept (M1).
    // Known gap: an undo while the request is in flight (premium, or a free
    // user through the top BackButton) puts one card back in the deck, so a
    // full page loses its last card to the cut: 1 card recorded as served
    // and never shown (accepted, PR5-slices section 14).
    const sliceEnd = remaining ?? held + limit;
    isFetchingFeedRef.current = true;
    const requestFeedKey = feedKey;
    // A failed or missing page counts as no progress, retried sooner.
    const retryAfterFailure = () => {
      failuresInARowRef.current += 1;
      stopAutoFetch(
        requestFeedKey,
        doublingDelay(
          FEED_FAILURE_RETRY_MS,
          FEED_FAILURE_RETRY_MAX_MS,
          failuresInARowRef.current,
        ),
      );
    };
    // The filters changed while the page was requested: it is dropped, and
    // the fetch effect runs again for the new filters.
    const isStale = () => requestFeedKey !== latestFeedKeyRef.current;

    try {
      const requestedAt = performance.now();
      failedFeedKeyRef.current = null;
      // The only window in which readFeedLimit gives the fetcher a limit.
      isListRequestRef.current = true;
      const pending = mutateFeed();
      isListRequestRef.current = false;
      const feed = await pending;
      if (!isMountedRef.current) return;
      if (isStale()) {
        setRefetchToken((token) => token + 1);
        return;
      }
      if (!feed || failedFeedKeyRef.current === requestFeedKey) {
        retryAfterFailure();
        return;
      }
      failuresInARowRef.current = 0;
      const loadTime = Math.round(performance.now() - requestedAt);

      setIsFetched(true);

      const resultCount = feed.results?.length || 0;
      analytics.trackFeedLoaded(resultCount, loadTime);

      const incoming = withoutSwipedIds(feed.results ?? [], swipedIds);
      const held = profilesRef.current;
      const heldIds = new Set(
        held.flatMap((item) => ('id' in item ? [item.id] : [])),
      );
      // Progress: the merge keeps a card the deck did not hold.
      const addsNewCard = mergeFeedProfiles(held, incoming, sliceEnd).some(
        (item) => 'id' in item && !heldIds.has(item.id),
      );

      setProfiles((prev) => mergeFeedProfiles(prev, incoming, sliceEnd));

      idlePagesInARowRef.current = addsNewCard
        ? 0
        : idlePagesInARowRef.current + 1;
      if (!addsNewCard || feed.hasMore === false) {
        stopAutoFetch(
          requestFeedKey,
          doublingDelay(
            FEED_RETRY_AFTER_MS,
            FEED_RETRY_MAX_MS,
            idlePagesInARowRef.current,
          ),
        );
      }
    } catch {
      if (!isMountedRef.current) return;
      if (isStale()) {
        setRefetchToken((token) => token + 1);
        return;
      }
      emit('error', {
        title: 'Ошибка загрузки анкет',
        description: 'Попробуйте позже',
      });
      retryAfterFailure();
    } finally {
      isFetchingFeedRef.current = false;
    }
  }, [emit, feedKey, mutateFeed, stopAutoFetch, swipedIds]);

  // A short deck refills. loadMoreProfiles fetches only while the deck has
  // room for another entry (free: a remaining swipe beyond the entries held),
  // so this runs again when the deck shrinks or the swipe status changes.
  useEffect(() => {
    if (isAutoFetchStopped) {
      return;
    }

    const profilesLength = profiles?.length || 0;

    if (!!auth?.user && profilesLength <= 5) {
      void loadMoreProfiles();
    }
  }, [
    auth?.user,
    auth?.swipe_status,
    isAutoFetchStopped,
    loadMoreProfiles,
    profiles?.length,
    refetchToken,
  ]);

  useEffect(() => {
    if (profiles[0] && !('ad' in profiles[0])) {
      const actionUser = profiles[0];

      tabbar.setBottomSheet(
        isInView ? (
          <BottomSheet
            buttons={[
              <IconButton
                testId="profiles-list-pass-button"
                size={500}
                variant="negativeSecondary"
                onClick={() => handleSwipeButtonClick('pass', actionUser)}
              >
                <CrossIcon />
              </IconButton>,
              <IconButton
                testId="profiles-list-like-button"
                size={500}
                variant="actionSecondary"
                onClick={() => handleSwipeButtonClick('like', actionUser)}
              >
                <HeartIcon />
              </IconButton>,
            ]}
            buttonDirection="row"
          />
        ) : undefined,
      );
    } else {
      tabbar.setBottomSheet(undefined);
    }
  }, [isInView, profiles]);

  useEffect(() => {
    const root = document.getElementById('root');
    if (!root) return;

    root.style.overscrollBehavior = 'none';

    return () => {
      root.style.overscrollBehavior = 'auto';
    };
  }, []);

  const handleSkipAdvertising = () => {
    setProfiles((prev) => (prev ?? []).filter((_, index) => index !== 0));

    cache.delete(feedAdQueryKey);
  };

  const pauseSwipes = useCallback((pauseMs: number) => {
    // A refusal that lands after the list unmounted pauses nothing.
    if (!isMountedRef.current) return;

    clearTimeout(swipePauseTimerRef.current);
    isSwipePausedRef.current = true;
    setIsSwipePaused(true);
    swipePauseTimerRef.current = setTimeout(() => {
      isSwipePausedRef.current = false;
      setIsSwipePaused(false);
    }, pauseMs);
  }, []);

  const handleProfileAction = useCallback(
    async (action: 'like' | 'pass', profile: User) => {
      const profileId = profile.id;

      document.querySelector('#root')?.scrollTo({ top: 0, behavior: 'smooth' });
      setIsShowDisclaimer(false);
      rememberSwipedId(swipedIds, profileId);
      // A swipe changes what the feed returns: the fetch effect may refill
      // the deck again.
      resumeAutoFetch();

      if (action === 'like') {
        setProfiles((prev) =>
          (prev ?? []).filter(
            (p): p is FeedResponse => 'ad' in p || p.id !== profileId,
          ),
        );
        analytics.trackSwipeLike(0);

        if (profile?.liked_me) {
          setIsShowMatchDialog(true);
          setMatchProfile(profile);
        }
      } else {
        analytics.trackSwipePass(0);
        setProfiles((prev) =>
          (prev ?? []).filter(
            (p): p is FeedResponse => 'ad' in p || p.id !== profileId,
          ),
        );
        setSkippedProfile(profile);

        if (profile?.liked_me) {
          setIsShowDisclaimer(true);
        }
      }

      // Whether this swipe was taken from the count. The count is read from
      // the store, not from `auth`: the bottom sheet's buttons keep the
      // handler of the render that last set them, so `auth` can be older
      // than a give-back below.
      let isSwipeTaken = false;

      try {
        const { swipe_status: swipeStatus, setSwipeStatus } =
          useAuthStore.getState();

        if (swipeStatus && swipeStatus.remaining !== null) {
          setSwipeStatus({
            ...swipeStatus,
            remaining: (swipeStatus.remaining ?? 0) - 1,
          });
          isSwipeTaken = true;
        }
        await triggerPostSwipe({
          action,
          target_user_id: profile.id,
        });
      } catch (e) {
        // The server did not record the swipe: a later page may serve the
        // card again.
        swipedIds.delete(String(profileId));

        if (isRateLimitedError(e)) {
          // Refused before it was counted: give back the swipe taken above,
          // or a last one would read as the daily limit.
          const { swipe_status: swipeStatus, setSwipeStatus } =
            useAuthStore.getState();

          if (isSwipeTaken && typeof swipeStatus?.remaining === 'number') {
            setSwipeStatus({
              ...swipeStatus,
              remaining: swipeStatus.remaining + 1,
            });
          }

          pauseSwipes(getRetryAfterMs(e) ?? SWIPE_RATE_LIMIT_PAUSE_MS);
          emit('info', SWIPE_RATE_LIMITED_NOTICE);

          return;
        }

        const error = e as { message: string };

        if (error?.message?.includes('429')) {
          const timeLeft = moment(auth?.swipe_status?.reset_time).fromNow(true);

          emit('error', {
            title: 'Лимит свайпов исчерпан',
            description: `До обновления свайпов осталось ${timeLeft}`,
          });

          setIsShowNoMoreSwipeError(true);

          const limitVal = auth?.swipe_status?.limit;
          const currRemaining = auth?.swipe_status?.remaining;
          const usedSwipes =
            typeof limitVal === 'number' && typeof currRemaining === 'number'
              ? limitVal - (currRemaining + 1)
              : null;
          postEvent(
            'first_paywall_impression',
            {
              source: 'swipe_limit_reached',
              swipes_until_paywall: usedSwipes,
              minutes_since_registration: null,
            },
            true,
          );
          postEvent('paywall_cycle_hit', {
            source: 'swipe_limit_reached',
            swipes_until_paywall: usedSwipes,
            minutes_since_registration: null,
          });

          return;
        }

        emit('error', {
          title: 'Ошибка действия',
          description: 'Попробуйте позже',
        });
      }
    },
    [auth, emit, pauseSwipes, resumeAutoFetch, swipedIds, triggerPostSwipe],
  );

  // The bottom sheet's buttons while swipes are paused only repeat the
  // notice; dragging a card is off then (canSwipes).
  const handleSwipeButtonClick = (action: 'like' | 'pass', profile: User) => {
    if (isSwipePausedRef.current) {
      emit('info', SWIPE_RATE_LIMITED_NOTICE);

      return;
    }

    handleProfileAction(action, profile);
  };

  const handleReturnSkippedProfile = () => {
    if (skippedProfile) {
      swipedIds.delete(String(skippedProfile.id));
      setProfiles([skippedProfile, ...(profiles ?? [])]);
      setSkippedProfile(undefined);
      setIsShowDisclaimer(false);
    }
  };

  const visibleProfiles = useMemo(() => {
    return (profiles ?? []).slice(0, VISIBLE_CARDS_COUNT);
  }, [profiles]);

  const isEnabledFeedAd = useMemo(
    () => visibleProfiles.some((item) => (item as FeedAdvertising)?.ad),
    [visibleProfiles],
  );

  const { data: dataFeedAd, isLoading: isLoadingFeedAd } = useGetFeedAd({
    enabled: isEnabledFeedAd,
    swrOptions: feedAdSWRRuntimeOptions,
  });

  const isLoadingProfiles = isLoadingFeed || isValidatingFeed;

  const isLoadingFinishedAndEmptyProfiles =
    !isLoadingProfiles && (profiles?.length ?? 0) === 0;

  const isNoMoreSwipes =
    (isLoadingFinishedAndEmptyProfiles && !hasSwipes) || isShowNoMoreSwipeError;

  const isEmptyResult =
    isLoadingFinishedAndEmptyProfiles &&
    isFetched &&
    getFiltersCount() === 0 &&
    hasSwipes;

  const isEmptyFilterResult =
    isLoadingFinishedAndEmptyProfiles &&
    isFetched &&
    getFiltersCount() > 0 &&
    hasSwipes;

  // Track empty feed states
  useEffect(() => {
    if (isEmptyResult) {
      analytics.trackFeedEmpty(false); // No filters applied
    } else if (isEmptyFilterResult) {
      analytics.trackFeedEmpty(true); // Filters applied but no results
    }
  }, [isEmptyResult, isEmptyFilterResult]);

  const swipeLeftTime = moment(auth?.swipe_status?.reset_time).fromNow(true);

  useEffect(() => {
    const hasAd = profiles.some((item) => 'ad' in item);

    if (isEnabledFeedAd && !isLoadingFeedAd && !dataFeedAd && hasAd) {
      setProfiles((prev) => (prev ?? []).filter((item) => !('ad' in item)));
    }
  }, [isEnabledFeedAd, isLoadingFeedAd, dataFeedAd, profiles]);

  return (
    <>
      {!isShowMatchDialog && (
        <>
          <div
            style={{
              position: 'relative',
              height: 'calc(100vh - 66px - 8px)',
              overflow: 'hidden',
            }}
          >
            {isLoadingProfiles && (
              <Loader
                style={{
                  zIndex: 1,
                }}
              />
            )}

            {isEmptyResult && (
              <Result
                src={'/icons/3d-icons/sand-clock.png'}
                title="Ждём новые анкеты"
                description={emptyResultDescriptionText}
                style={{
                  zIndex: 1,
                }}
                button={currentButtons}
                iconContainerBg="rgba(255, 92, 140, 0.07)"
              />
            )}

            {isEmptyFilterResult && (
              <Result
                src={'/icons/3d-icons/sand-clock.png'}
                title="Не нашли анкеты"
                description="Попробуйте изменить или сбросить фильтры"
                style={{
                  zIndex: 1,
                }}
                iconContainerBg="rgba(255, 92, 140, 0.07)"
              />
            )}

            {isNoMoreSwipes && (
              <Result
                src={'/icons/3d-icons/sand-clock.png'}
                title="Свайпы закончились"
                description={
                  <>
                    Новые появятся через {swipeLeftTime}.
                    <br />
                    {activeTasksCount > 0
                      ? 'Чтобы не ждать, выполни задания или подключи Premium'
                      : 'Чтобы не ждать, подключи Premium'}
                  </>
                }
                style={{
                  zIndex: 1,
                }}
                iconContainerBg="rgba(255, 92, 140, 0.07)"
                button={currentButtons}
              />
            )}

            <BackButton
              active={isShowDisclaimer}
              visible={!!skippedProfile && !isNoMoreSwipes}
              onClick={handleReturnSkippedProfile}
              variant="actionSecondary"
              style={{
                position: 'absolute',
                zIndex: 1,
                top: 16,
                left: 16,
              }}
            />

            {isNoMoreSwipes ? null : (
              <FiltersButton
                filtersCount={getFiltersCount()}
                onClick={() => navigate({ to: '/dating/filters' })}
                variant="actionSecondary"
                style={{
                  position: 'absolute',
                  zIndex: 1,
                  top: 16,
                  right: 16,
                }}
              />
            )}

            {(visibleProfiles ?? []).map((profile, index) => {
              if ('ad' in profile) {
                const adStyles: React.CSSProperties = {
                  position: 'absolute',
                  zIndex: index === 0 ? 99 : visibleProfiles.length - index,
                  pointerEvents: index === 0 ? 'auto' : 'none',
                };

                if (isLoadingFeedAd) {
                  return <AdvertisingCardSkeleton style={adStyles} />;
                }

                if (dataFeedAd) {
                  return (
                    <AdvertisingCard
                      key={`profiles-list-ad-${dataFeedAd.ad.id}`}
                      style={adStyles}
                      profile={profile}
                      data={dataFeedAd}
                      isActive={index === 0}
                      onSkip={handleSkipAdvertising}
                    />
                  );
                }

                return null;
              }

              return (
                <ProfileCard
                  key={profile.id}
                  canSwipes={!isSwipePaused}
                  style={{
                    position: 'absolute',
                    zIndex: visibleProfiles.length - index,
                    pointerEvents: index === 0 ? 'auto' : 'none',
                  }}
                  profile={profile}
                  backButton={
                    <BackButton
                      active={isShowDisclaimer}
                      visible={!!skippedProfile}
                      onClick={() =>
                        auth?.user?.is_premium
                          ? handleReturnSkippedProfile()
                          : navigate({
                              to: '/premium',
                            })
                      }
                      style={{
                        background: 'var(--bg-secondary-on-dark-transparent)',
                      }}
                    />
                  }
                  filtersButton={
                    <FiltersButton
                      filtersCount={getFiltersCount()}
                      onClick={() =>
                        navigate({
                          to: '/dating/filters',
                        })
                      }
                      style={{
                        background: 'var(--bg-secondary-on-dark-transparent)',
                      }}
                    />
                  }
                  onLike={(profile) => handleProfileAction('like', profile)}
                  onSkip={(profile) => handleProfileAction('pass', profile)}
                />
              );
            })}
          </div>

          <div ref={ref}>
            {(visibleProfiles?.length ?? 0) &&
              !('ad' in visibleProfiles[0]) && (
                <ProfileInfo profile={visibleProfiles[0]} />
              )}
          </div>

          <div
            style={{
              position: 'absolute',
              top: 66,
              left: 20,
              right: 20,
              zIndex: 500,
            }}
          >
            <Disclaimer
              title="Пропущенный мэтч"
              body="Нажми назад, чтобы вернуть его"
              type="info"
              visible={isShowDisclaimer}
              style={{
                width: 'fit-content',
                margin: 'auto',
                background: 'var(--graphic-primary-on-dark-transparent)',
              }}
            />
          </div>
        </>
      )}

      <AnimatePresence>
        {isShowMatchDialog && matchProfile && (
          <MatchDialog
            matchProfile={matchProfile}
            onClose={() => setIsShowMatchDialog(false)}
          />
        )}
      </AnimatePresence>
    </>
  );
};

ProfilesList.displayName = 'ProfilesList';
